import type { DashboardData } from "../../shared/types";

export type PublicConfig = {
  appName: string;
  environment: "local" | "preview" | "pilot" | "production";
  previewEnabled: boolean;
  supabaseUrl: string;
  supabasePublishableKey: string;
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(message: string, status: number, code = "request_failed") {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export async function getPublicConfig(): Promise<PublicConfig> {
  const response = await fetch("/api/config", { credentials: "same-origin" });
  return parseResponse<PublicConfig>(response);
}

export async function apiRequest<T>(
  path: string,
  accessToken: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
  });

  return parseResponse<T>(response);
}

export function loadDashboard(accessToken: string): Promise<DashboardData> {
  return apiRequest<DashboardData>("/api/dashboard", accessToken);
}

async function parseResponse<T>(response: Response): Promise<T> {
  const body = (await response.json().catch(() => null)) as
    | { error?: { code?: string; message?: string } }
    | T
    | null;

  if (!response.ok) {
    const failure = body as { error?: { code?: string; message?: string } } | null;
    throw new ApiError(
      failure?.error?.message ?? "AutoSocial could not complete this request.",
      response.status,
      failure?.error?.code,
    );
  }

  return body as T;
}
